'use client';

import React, { useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ZoomInIcon,
  ZoomOutIcon,
  DownloadIcon,
  FileTextIcon,
  EnterFullScreenIcon,
} from '@radix-ui/react-icons';

import { Box } from '../box';
import { IconButton } from '../icon-button';
import { Stack } from '../stack';
import { Text } from '../text';
import { Input } from '../input';

export interface PdfViewerProps {
  /** PDF file URL or File object */
  src: string | File;
  /** Initial page number */
  initialPage?: number;
  /** Initial zoom level */
  initialZoom?: number;
  /** Whether to show controls */
  showControls?: boolean;
  /** Whether to show thumbnails */
  showThumbnails?: boolean;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when PDF is loaded */
  onLoad?: (numPages: number) => void;
  /** Callback when PDF fails to load */
  onError?: (error: Error) => void;
}

/**
 * PdfViewer component - displays PDF documents with navigation controls
 * Note: This is a simplified implementation. For production, use react-pdf or similar library
 */
export const PdfViewer: React.FC<PdfViewerProps> = ({
  src,
  initialPage = 1,
  initialZoom = 1,
  showControls = true,
  showThumbnails = false,
  onPageChange,
  onLoad,
  onError: _onError,
  ...boxProps
}) => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(initialZoom);
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);

  // This is a placeholder implementation
  // In a real implementation, you would use a library like react-pdf
  React.useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
      setTotalPages(10); // Mock total pages
      onLoad?.(10);
    }, 1000);
  }, [src, onLoad]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      onPageChange?.(page);
    }
  };

  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const page = parseInt(e.target.value, 10);
    if (!isNaN(page)) {
      goToPage(page);
    }
  };

  const handleDownload = () => {
    if (typeof src === 'string') {
      const link = document.createElement('a');
      link.href = src;
      link.download = 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (typeof src === 'string') {
      window.open(src, '_blank')?.print();
    }
  };

  const handleFullscreen = () => {
    const element = document.getElementById('pdf-viewer-container');
    if (element) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        element.requestFullscreen();
      }
    }
  };

  return (
    <Box
      id="pdf-viewer-container"
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
      backgroundColor="bg.muted"
      borderRadius="lg"
      overflow="hidden"
      {...boxProps}
    >
      {/* Controls */}
      {showControls && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding={3}
          backgroundColor="bg.surface"
          borderBottom="1px solid"
          borderColor="border.muted"
        >
          {/* Navigation controls */}
          <Stack direction="row" gap={2} alignItems="center">
            <IconButton
              size="sm"
              variant="ghost"
              onClick={goToPreviousPage}
              disabled={currentPage <= 1}
              aria-label="Previous page"
              icon={<ChevronLeftIcon />}
            />

            <Box display="flex" alignItems="center" gap={2}>
              <Input
                type="number"
                value={currentPage}
                onChange={handlePageInputChange}
                min={1}
                max={totalPages}
                width="60px"
                textAlign="center"
              />
              <Text fontSize="sm" color="fg.muted">
                of {totalPages}
              </Text>
            </Box>

            <IconButton
              size="sm"
              variant="ghost"
              onClick={goToNextPage}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
              icon={<ChevronRightIcon />}
            />
          </Stack>

          {/* Zoom controls */}
          <Stack direction="row" gap={2} alignItems="center">
            <IconButton
              size="sm"
              variant="ghost"
              onClick={zoomOut}
              disabled={zoom <= 0.5}
              aria-label="Zoom out"
              icon={<ZoomOutIcon />}
            />

            <Text fontSize="sm" fontWeight="medium" minWidth="60px" textAlign="center">
              {Math.round(zoom * 100)}%
            </Text>

            <IconButton
              size="sm"
              variant="ghost"
              onClick={zoomIn}
              disabled={zoom >= 3}
              aria-label="Zoom in"
              icon={<ZoomInIcon />}
            />
          </Stack>

          {/* Action controls */}
          <Stack direction="row" gap={1}>
            <IconButton
              size="sm"
              variant="ghost"
              onClick={handleDownload}
              aria-label="Download PDF"
              title="Download"
              icon={<DownloadIcon />}
            />

            <IconButton
              size="sm"
              variant="ghost"
              onClick={handlePrint}
              aria-label="Print PDF"
              title="Print"
              icon={<FileTextIcon />}
            />

            <IconButton
              size="sm"
              variant="ghost"
              onClick={handleFullscreen}
              aria-label="Fullscreen"
              title="Fullscreen"
              icon={<EnterFullScreenIcon />}
            />
          </Stack>
        </Box>
      )}

      {/* Main content area */}
      <Box display="flex" flex={1} overflow="hidden">
        {/* Thumbnails sidebar */}
        {showThumbnails && (
          <Box
            width="150px"
            backgroundColor="bg.surface"
            borderRight="1px solid"
            borderColor="border.muted"
            overflowY="auto"
            padding={2}
          >
            <Stack gap={2}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Box
                  key={page}
                  padding={2}
                  backgroundColor={page === currentPage ? 'bg.primary.muted' : 'bg.muted'}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => goToPage(page)}
                  border="1px solid"
                  borderColor={page === currentPage ? 'border.primary' : 'border.muted'}
                  transition="all 0.2s"
                  _hover={{
                    borderColor: 'border.default',
                  }}
                >
                  <Box height="100px" backgroundColor="white" borderRadius="sm" marginBottom={1} />
                  <Text fontSize="xs" textAlign="center">
                    Page {page}
                  </Text>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* PDF content area */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={4}
          overflow="auto"
        >
          {isLoading && <Text color="fg.muted">Loading PDF...</Text>}

          {error && (
            <Stack gap={2} alignItems="center">
              <Text color="fg.error">Failed to load PDF</Text>
              <Text fontSize="sm" color="fg.muted">
                {error}
              </Text>
            </Stack>
          )}

          {!isLoading && !error && (
            <Box
              backgroundColor="white"
              boxShadow="lg"
              borderRadius="md"
              width="100%"
              maxWidth="800px"
              aspectRatio="8.5/11"
              transform={`scale(${zoom})`}
              transformOrigin="top center"
              transition="transform 0.2s"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="#9CA3AF" fontSize="2xl">
                PDF Page {currentPage}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PdfViewer;
